const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3000";

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

async function request<T>(
  method: HttpMethod,
  path: string,
  body?: unknown,
  token?: string,
  extraHeaders?: Record<string, string>
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...extraHeaders,
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem("easly_auth");
      window.location.href = "/login";
      throw new Error("Unauthorized");
    }
    const error = await response.json().catch(() => ({ message: "Request failed" }));
    throw new ApiError(error.message ?? "Request failed", response.status, error.code);
  }

  const text = await response.text();
  return text ? JSON.parse(text) : undefined;
}

export const api = {
  get: <T>(path: string, token?: string, extraHeaders?: Record<string, string>) =>
    request<T>("GET", path, undefined, token, extraHeaders),
  post: <T>(path: string, body: unknown, token?: string, extraHeaders?: Record<string, string>) =>
    request<T>("POST", path, body, token, extraHeaders),
  put: <T>(path: string, body: unknown, token?: string, extraHeaders?: Record<string, string>) =>
    request<T>("PUT", path, body, token, extraHeaders),
  patch: <T>(path: string, body: unknown, token?: string, extraHeaders?: Record<string, string>) =>
    request<T>("PATCH", path, body, token, extraHeaders),
  delete: <T>(path: string, token?: string, extraHeaders?: Record<string, string>) =>
    request<T>("DELETE", path, undefined, token, extraHeaders),
};
